/**
 * The old-article shapes the desk editor carries (native publishing, Task 13). Each string is exactly
 * what the server's body sanitiser (sanitize.clean_body) keeps, so a body the editor writes is the
 * body the server stores:
 *
 * - a video figure, as article_cleaner.embed_figure writes it;
 * - a gallery of images;
 * - the locked block, an empty wrapper the server fills with its own copy of what was there.
 *
 * Tables need a little help: TipTap's table has no caption, thead or tfoot and puts a <p> in every
 * cell. prepareBody moves the caption and each row's section onto attributes the schema reads, and
 * finishBody turns the schema's table back into the original shape, so opening and saving a table
 * changes nothing.
 */

export type VideoProvider = 'youtube' | 'vimeo'
export type GalleryImage = { src: string; alt: string; width?: number | null; height?: number | null }

/** sanitize.escape_attr: the same five replacements, in the same order. */
export const escAttr = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\u00a0/g, '&nbsp;')

const escText = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\u00a0/g, '&nbsp;')

const NAMED: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: '\u00a0', '#39': "'" }
export function decodeEntities(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z]+[0-9]*);/gi, (m, e: string) => {
    if (e[0] === '#') {
      const n = e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10)
      return Number.isFinite(n) && n > 0 && n <= 0x10ffff ? String.fromCodePoint(n) : m
    }
    return NAMED[e.toLowerCase()] ?? m
  })
}

/** The video ids the server accepts, per provider (sanitize._VIDEO_ID). */
export const VIDEO_ID: Record<VideoProvider, RegExp> = { youtube: /^[A-Za-z0-9_-]{6,20}$/, vimeo: /^[0-9]{4,12}$/ }

export const embedUrl = (provider: VideoProvider, id: string): string =>
  provider === 'youtube' ? `https://www.youtube-nocookie.com/embed/${id}` : `https://player.vimeo.com/video/${id}`

/** article_cleaner.embed_figure, byte for byte. */
export function videoFigureHtml(provider: VideoProvider, id: string): string {
  if (!VIDEO_ID[provider]?.test(id)) throw new Error(`not a ${provider} video id`)
  const title = provider === 'youtube' ? 'YouTube video' : 'Vimeo video'
  const iframe = `<iframe src="${embedUrl(provider, id)}" loading="lazy" allowfullscreen `
    + `title="${title}" allow="accelerometer; encrypted-media; picture-in-picture"></iframe>`
  return `<figure class="wp-embed" data-provider="${provider}" data-id="${id}">${iframe}</figure>`
}

const DIMENSION = /^[1-9][0-9]{0,4}$/

/** One gallery image's tag, as the sanitiser keeps it: src, alt, then width and height when valid. */
export function galleryImgHtml(img: GalleryImage): string {
  let out = `<img src="${escAttr(img.src)}" alt="${escAttr(img.alt ?? '')}"`
  for (const dim of ['width', 'height'] as const) {
    const v = img[dim]
    if (v != null && DIMENSION.test(String(v))) out += ` ${dim}="${v}"`
  }
  return out + '>'
}

export function galleryHtml(images: GalleryImage[]): string {
  return `<figure class="wp-gallery">${images.map(galleryImgHtml).join('')}</figure>`
}

export const LOCKED_CLASS = 'desk-locked'
export const lockedHtml = (key: string): string => `<div class="${LOCKED_CLASS}" data-key="${escAttr(key)}"></div>`

/** A locked block's text for the editor to show: whitespace collapsed, cut at a word, with "…". */
export function lockedPreview(text: string, max = 140): string {
  const t = text.replace(/\s+/g, ' ').trim()
  if (t.length <= max) return t
  const cut = t.slice(0, max)
  const space = cut.lastIndexOf(' ')
  return `${(space > 0 ? cut.slice(0, space) : cut).trimEnd()}…`
}

export const LOCKED_FALLBACK = 'Something the editor can’t change here. It is saved exactly as it was; you can move it or remove it.'

/** What a locked block shows: the server's preview of it (DraftDetail.locked_previews), else a
 *  plain line. Shown as text only. */
export const lockedPreviewFor = (previews: Record<string, string> | undefined, key: string): string =>
  (previews && Object.prototype.hasOwnProperty.call(previews, key) && previews[key].trim()) || LOCKED_FALLBACK

/** html_allowlist.SAFE_ID: the only ids the server keeps (an anchor, never a page global's name). */
const SAFE_ID = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/
export const safeId = (v: string | null | undefined): string | null => (v != null && SAFE_ID.test(v) ? v : null)

// --- Tables -------------------------------------------------------------------------------

/** A start or end tag (attributes double- or single-quoted or bare), or a comment. */
const TAG = /<!--[\s\S]*?-->|<(\/?)([a-zA-Z][a-zA-Z0-9]*)((?:\s+[^\s=>/]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+))?)*)\s*(\/?)>/g
const ATTR = /\s+([^\s=>/]+)(?:\s*=\s*("[^"]*"|'[^']*'|[^\s>]+))?/g

type Attr = { name: string; raw: string; value: string }
function attrsOf(s: string): Attr[] {
  const out: Attr[] = []
  for (const m of s.matchAll(ATTR)) {
    const v = m[2] ?? ''
    const unq = v.startsWith('"') || v.startsWith("'") ? v.slice(1, -1) : v
    out.push({ name: m[1].toLowerCase(), raw: m[0], value: decodeEntities(unq) })
  }
  return out
}

type Tok = { text: string; close: boolean; name: string; attrs: string }
function tokens(html: string): (Tok | string)[] {
  const out: (Tok | string)[] = []
  let at = 0
  for (const m of html.matchAll(TAG)) {
    if (m.index > at) out.push(html.slice(at, m.index))
    out.push(m[2] ? { text: m[0], close: m[1] === '/', name: m[2].toLowerCase(), attrs: m[3] ?? '' } : m[0])
    at = m.index + m[0].length
  }
  if (at < html.length) out.push(html.slice(at))
  return out
}

/** Before the editor reads a body: a table's caption becomes its data-caption, and each row in a
 *  thead or tfoot is marked data-section="head" or "foot". Nothing else changes. */
export function prepareBody(html: string): string {
  if (!/<table[\s>]/i.test(html)) return html
  const out: string[] = []
  const tables: { at: number; section: 'head' | 'foot' | null; caption: string[] | null; captionDepth: number }[] = []
  for (const t of tokens(html)) {
    const top = tables[tables.length - 1]
    if (top?.caption) {             // inside a caption: keep its text only
      if (typeof t === 'string') { if (!t.startsWith('<!--')) top.caption.push(decodeEntities(t)) }
      else if (t.name === 'caption') {
        top.captionDepth += t.close ? -1 : 1
        if (top.captionDepth === 0) {
          const text = top.caption.join('').replace(/\s+/g, ' ').trim()
          if (text) out[top.at] = out[top.at].replace(/\s*\/?>$/, ` data-caption="${escAttr(text)}">`)
          top.caption = null
        }
      }
      continue
    }
    if (typeof t === 'string') { out.push(t); continue }
    if (t.name === 'table') {
      if (t.close) tables.pop()
      else tables.push({ at: out.length, section: null, caption: null, captionDepth: 0 })
      out.push(t.text)
    } else if (top && t.name === 'caption' && !t.close) {
      top.caption = []
      top.captionDepth = 1
    } else if (top && (t.name === 'thead' || t.name === 'tfoot' || t.name === 'tbody')) {
      top.section = t.close || t.name === 'tbody' ? null : t.name === 'thead' ? 'head' : 'foot'
      out.push(t.text)
    } else if (top?.section && t.name === 'tr' && !t.close) {
      out.push(`<tr data-section="${top.section}"${t.attrs}>`)
    } else {
      out.push(t.text)
    }
  }
  return out.join('')
}

/** After the editor writes a body: each table gets its caption back, its rows regrouped into
 *  thead / tbody / tfoot, no colgroup or style, and a cell holding one plain paragraph holds just
 *  its text again; a list item holding one plain paragraph holds just its text too. A fixed point. */
export function finishBody(html: string): string {
  return unwrapListItems(/<table[\s>]/i.test(html) ? finishTables(html) : html)
}

/** A list item's leading attribute-less paragraph, when nothing but the item's end or a nested
 *  list follows it: just its content, as WordPress writes lists (<li>x</li>, <li>x<ul>…</ul></li>).
 *  TipTap reads that back as li > p, so this is a fixed point. Two paragraphs, or a paragraph with
 *  an id, stay as they are. */
export function unwrapListItems(html: string): string {
  if (!/<li[\s>]/i.test(html)) return html
  return html.replace(
    /(<li(?:\s[^>]*)?>)<p>((?:(?!<\/?(?:p|li|ul|ol)[\s>/]).)*?)<\/p>(?=<\/li>|<(?:ul|ol)[\s>])/gs, '$1$2')
}

function finishTables(html: string): string {
  const out: string[] = []
  type T = { group: 'head' | 'body' | 'foot' | null; open: 'head' | 'body' | 'foot' | null; dropping: number }
  const tables: T[] = []
  for (const t of tokens(html)) {
    const top = tables[tables.length - 1]
    if (top?.dropping) {            // inside a colgroup
      if (typeof t !== 'string' && t.name === 'colgroup') top.dropping += t.close ? -1 : 1
      continue
    }
    if (typeof t === 'string') { out.push(t); continue }
    if (t.name === 'table' && !t.close) {
      const attrs = attrsOf(t.attrs)
      const caption = attrs.find((a) => a.name === 'data-caption')?.value.trim() ?? ''
      const kept = attrs.filter((a) => a.name !== 'data-caption' && a.name !== 'style').map((a) => a.raw).join('')
      out.push(`<table${kept}>${caption ? `<caption>${escText(caption)}</caption>` : ''}`)
      tables.push({ group: null, open: null, dropping: 0 })
    } else if (t.name === 'table') {
      if (top?.open) out.push(`</t${top.open}>`)
      tables.pop()
      out.push(t.text)
    } else if (top && t.name === 'colgroup' && !t.close) {
      top.dropping = 1
    } else if (top && t.name === 'col') {
      // a stray col outside a colgroup: dropped too
    } else if (top && (t.name === 'thead' || t.name === 'tbody' || t.name === 'tfoot')) {
      top.group = t.close ? null : t.name === 'thead' ? 'head' : t.name === 'tfoot' ? 'foot' : 'body'
    } else if (top && t.name === 'tr' && !t.close) {
      const attrs = attrsOf(t.attrs)
      const marked = attrs.find((a) => a.name === 'data-section')?.value
      const sec = marked === 'head' || marked === 'foot' ? marked : top.group ?? 'body'
      if (top.open !== sec) {
        if (top.open) out.push(`</t${top.open}>`)
        out.push(`<t${sec}>`)
        top.open = sec
      }
      out.push(`<tr${attrs.filter((a) => a.name !== 'data-section').map((a) => a.raw).join('')}>`)
    } else {
      out.push(t.text)
    }
  }
  // A cell holding exactly one attribute-less paragraph: just its content.
  return out.join('').replace(
    /(<t[dh](?:\s[^>]*)?>)<p>((?:(?!<\/?p[\s>/]|<\/?t[dh][\s>]|<\/?table[\s>]).)*?)<\/p>(?=<\/t[dh]>)/gs, '$1$2')
}
